<template lang="pug">
    component(:is="tag", class="_bdg", v-on="listeners")
        slot
        transition(
            name="_tr-scale",
            appear,
            @after-leave="reset")
            span(
                class="_bdgw",
                :class="{ _bdgi: !$slots.default, ['bg-'+tone]: tone, ['_e'+appearance]: appearance }",
                :style="style",
                v-show="value",
                aria-atomic="true",
                :aria-label="$locale.data.Badge",
                aria-live="polite",
                role="status")
                slot(name="label") {{ivalue}}
</template>

<script>
    import $num from '../../prototype/num';
    import listeners from '../private/listeners';


    /**
     * Displays a number, text, or icon, floating around content aligned to one of the corners.
     * The information displayed is typically status related to the adjoining content. Place the
     * content you wish to float the badge around inside the default slot. To create a badge with
     * an icon, place the icon inside the `label` slot. If you want to create a badge that is not
     * floating with relation to some content (i.e. inline), please consider `ib-tag`.
     */
    export default {
        name: 'ib-badge2',

        mixins: [
            listeners
        ],

        props: {
            /**
             * The current value displayed in the badge.
             */
            value: {
                type: [ Boolean, Number, String ],
                default: '!'
            },
            /**
             * Maximum value to display if value is a `Number`; may be `undefined`.
             */
            max: {
                type: Number,
                default: 100
            },
            /**
             * Position relative to content [top, bottom]; default is top.
             */
            position: {
                type: String,
                default: 'top'
            },
            /**
             * Alignment relative to content [start, end, left, right]; default is end.
             * This is advisory and alignment can change to accommodate screen space.
             */
            align: {
                type: String,
                default: 'end'
            },
            /**
             * Horizontal position adjustment in pixels, away from the content.
             */
            offsetx: {
                type: Number,
                default: 0
            },
            /**
             * Vertical position adjustment in pixels, away from the content.
             */
            offsety: {
                type: Number,
                default: 0
            },
            /**
             * If `true`, grow toward the content rather than away from it as the size of the
             * badge increases.
             */
            inward: Boolean,
            /**
             * Semantic color class.
             */
            tone: String,
            /**
             * Appearance modifier (`dot`, `warning`, `arrow`, `icon`, `icon-lg`, and `rectangle` are supported).
             */
            appearance: String,
            /**
             * Wrapper tag.
             */
            tag: {
                type: String,
                default: 'span'
            }
        },

        data() {
            return {
                ivalue: null
            }
        },

        computed: {
            style() {
                const rtl = this.$rtl;
                const dot = this.appearance == 'dot';
                const position = this.position;
                const offsetx = this.offsetx;
                const offsety = this.offsety;
                const align = {
                    start: rtl ? 'right' : 'left',
                    end: rtl ? 'left' : 'right'
                }[this.align] || this.align;
                let r = {};
                r[position] = -offsety - (dot ? 5 : 8) + 'px';
                if (dot || this.inward) {
                    r[align] = -offsetx - (dot ? 5 : 6) + 'px';
                } else {
                    r[{ right: 'left', left: 'right' }[align]] = 'calc(100% - ' + (12 - offsetx) + 'px)';
                }
                return r;
            }
        },

        created() {
            if ($build.env != 'production') {
                const position = this.position;
                if (position != 'top' && position != 'bottom') {
                    throw new RangeError('Invalid position value');
                }
                const align = this.align;
                if (align != 'start' && align != 'end' && align != 'left' && align != 'right') {
                    throw new RangeError('Invalid align value');
                }
            }
        },

        watch: {
            value: {
                handler: 'update',
                immediate: true
            }
        },

        methods: {
            reset() {
                this.ivalue = 0;
            },

            update() {
                const to = this.value;
                if (to) {  // unless empty string, null, undefined, or 0
                    this.ivalue = typeof to === 'number'
                        ? to < this.max
                            ? $num.int(to)
                            : $num.int(this.max) + '+'
                        : to;
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._bdg {
        display: inline-block;
        position: relative;
        /** TODO:REMOVE */
        > ._btn,
        > ._field,
        > ._pick {
            margin: 0;
            max-width: 100%;
        }
    }

    ._bdgw {
        position: absolute;
        pointer-events: none;
        padding: 3px;
        min-width: calc(~'1em + 6px');
        border-radius: 99px;
        line-height: 1;
        .font-size(11);
        font-weight: 600;
        background-color: @bg-error;
        color: #fff;
        text-align: center;
        text-indent: 0;
        white-space: nowrap;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        z-index: @z-badge;

        &._edot {
            width: 11px;
            height: 11px;
            padding: 0;
            font-size: 0;
            > * {
                display: none;
            }
        }
        &._earrow {
            border: none;
            border-radius: 0;
            padding: 2px 8px;
            -webkit-padding-start: 4px;
            padding-inline-start: 4px;
            &::before {
                content: '';
                position: absolute;
                display: inline-block;
                top: 0;
                width: calc(~'.8839em + 2px');
                height: 70.7107%;  /** 1 / sqrt(2) */
                background-color: inherit;
                z-index: -1;
            }
        }
        &._ewarning {
            background: none;
            box-shadow: none;
            padding: 5px;
            .font-size(@fs6);
            z-index: 0;
            color: #000;
            &::before {
                content: '';
                position: absolute;
                top: 4px;
                left: calc(~'50% - .5em');
                width: 1em;
                height: 1em;
                transform: rotate(45deg);
                background-color: @bg-warning;
                border-radius: 2px;
                box-shadow: 2px 2px 4px rgba(0,0,0,0.2);
                z-index: -1;
            }
        }
        &._eicon {
            padding: 1px;
        }
        &._eicon-lg {
            padding: 1px;
            .font-size(@fs6);
        }
        &._erectangle {
            border-radius: 0;
        }
    }

    [dir=ltr] ._bdgw,
    [dir=rtl] [dir=ltr] ._bdgw {
        &._earrow {
            transform-origin: 0 50%;
            &::before {
                left: 0;
                transform-origin: 0 0;
                transform: rotate(45deg);
            }
        }
    }

    [dir=rtl] ._bdgw,
    [dir=ltr] [dir=rtl] ._bdgw {
        &._earrow {
            transform-origin: 100% 50%;
            &::before {
                right: 0;
                transform-origin: 100% 0;
                transform: rotate(-45deg);
            }
        }
    }

    ._bdgi {
        position: static;
        pointer-events: auto;
    }
</style>
